import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPageContent, subscribeToPage } from '../firebase/firebase-config';

function QuestionPapers() {
  const [content, setContent] = useState({ 
    questionPapers: [], 
    title: 'Previous Year Question Papers',
    sliderImages: [] 
  });
  const [filter, setFilter] = useState({ class: '', subject: '', examType: '', year: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getPageContent('questionPapers');
        setContent(prev => ({ ...prev, ...data }));
      } catch (error) { 
        console.error(error); 
      } finally { 
        setLoading(false); 
      }
    };
    loadData();
    const unsubscribe = subscribeToPage('questionPapers', (data) => { 
      if (data) setContent(prev => ({ ...prev, ...data })); 
    });
    return () => unsubscribe();
  }, []);

  const questionPapers = content.questionPapers || [];
  
  // Get unique values for filters
  const uniqueClasses = [...new Set(questionPapers.map(p => p.class).filter(Boolean))].sort();
  const uniqueSubjects = [...new Set(questionPapers.map(p => p.subject).filter(Boolean))].sort();
  const uniqueYears = [...new Set(questionPapers.map(p => p.year).filter(Boolean))].sort().reverse();
  const examTypes = ['Annual', 'Half-Yearly', 'Quarterly', 'Pre-Board', 'Monthly Test'];

  const filteredPapers = questionPapers.filter(paper => {
    if (filter.class && paper.class !== filter.class) return false;
    if (filter.subject && paper.subject !== filter.subject) return false;
    if (filter.examType && paper.examType !== filter.examType) return false;
    if (filter.year && paper.year !== filter.year) return false;
    return true;
  });

  const clearFilters = () => {
    setFilter({ class: '', subject: '', examType: '', year: '' });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Question Papers...</p>
      </div>
    );
  }

  return (
    <div className="papers-page">
      
        <div className="container">
          <h1>{content.title}</h1>
          <div className="hero-divider"></div>
          <p>Previous Year Papers • Practice Materials • Exam Preparation</p>
        </div>
      

      <div className="container">
        {/* Filters Section */}
        <div className="filters-section">
          <h3>Filter Question Papers</h3>
          <div className="filters-grid">
            <select 
              value={filter.class} 
              onChange={(e) => setFilter({...filter, class: e.target.value})}
              className="filter-select"
            >
              <option value="">All Classes</option>
              {uniqueClasses.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            
            <select 
              value={filter.subject} 
              onChange={(e) => setFilter({...filter, subject: e.target.value})}
              className="filter-select"
            >
              <option value="">All Subjects</option>
              {uniqueSubjects.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            
            <select 
              value={filter.examType} 
              onChange={(e) => setFilter({...filter, examType: e.target.value})}
              className="filter-select"
            >
              <option value="">All Exams</option>
              {examTypes.map(e => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
            
            <select 
              value={filter.year} 
              onChange={(e) => setFilter({...filter, year: e.target.value})}
              className="filter-select"
            >
              <option value="">All Years</option>
              {uniqueYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear Filters
            </button>
          </div>
        </div>

        {/* Results Count */}
        <div className="results-count">
          Showing {filteredPapers.length} of {questionPapers.length} question papers
        </div>

        {/* Question Papers Grid */}
        {filteredPapers.length > 0 ? (
          <div className="papers-grid">
            {filteredPapers.map((paper) => (
              <div key={paper.id} className="paper-card">
                <div className="paper-badge">
                  <span className="badge-class">{paper.class}</span>
                  <span className="badge-exam" style={{ background: getExamColor(paper.examType) }}>
                    {paper.examType}
                  </span>
                </div>
                <h3 className="paper-subject">{paper.subject}</h3>
                <div className="paper-meta">
                  <span className="meta-year">📅 {paper.year}</span>
                  <span className="meta-type">📄 Question Paper</span>
                </div>
                {paper.file && paper.file.url ? (
                  <a 
                    href={paper.file.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="download-btn"
                  >
                    📥 Download Paper
                  </a>
                ) : (
                  <span className="not-available">Coming Soon</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📄</div>
            <h3>No Question Papers Found</h3>
            <p>No question papers match your filter criteria. Try adjusting your filters.</p>
            <button className="clear-filters-btn" onClick={clearFilters}>Clear All Filters</button>
          </div>
        )}

        {/* Info Section */}
        <div className="info-section">
          <div className="info-card">
            <div className="info-icon">📝</div>
            <h4>Exam Preparation Tips</h4>
            <p>Practice with previous year papers to understand exam pattern and important topics</p>
          </div>
          <div className="info-card">
            <div className="info-icon">📚</div>
            <h4>Regular Updates</h4>
            <p>New question papers are added regularly. Check back for the latest materials</p>
          </div>
          <div className="info-card">
            <div className="info-icon">🖨️</div>
            <h4>Print & Practice</h4>
            <p>Download and print papers for offline practice</p>
          </div>
        </div>

        {/* Back to Academics Link */}
        <div className="back-link">
          <Link to="/academics" className="back-btn">
            ← Back to Academics
          </Link>
        </div>
      </div>

      <style>{`
        .papers-page {
          background: #f8fafc;
          min-height: 100vh;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e2e8f0;
          border-top-color: #1a3a6b;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 24px 60px;
        }

        /* Page Hero */
        .page-hero {
          background: linear-gradient(135deg, #1a3a6b, #f59e0b);
          color: white;
          text-align: center;
          padding: 60px 20px;
          margin-bottom: 40px;
        }

        .page-hero h1 {
          font-size: 48px;
          margin-bottom: 20px;
          font-weight: 700;
        }

        .hero-divider {
          width: 60px;
          height: 4px;
          background: white;
          margin: 0 auto 20px;
          border-radius: 2px;
        }

        .page-hero p {
          font-size: 18px;
          opacity: 0.9;
        }

        /* Filters Section */
        .filters-section {
          background: white;
          border-radius: 20px;
          padding: 25px;
          margin-bottom: 30px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .filters-section h3 {
          color: #1a3a6b;
          margin-bottom: 20px;
          font-size: 18px;
        }

        .filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 15px;
        }

        .filter-select {
          padding: 10px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          background: white;
          font-size: 14px;
          cursor: pointer;
        }

        .filter-select:focus {
          outline: none;
          border-color: #1a3a6b;
        }

        .clear-filters-btn {
          background: #64748b;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s;
        }

        .clear-filters-btn:hover {
          background: #ef4444;
        }

        /* Results Count */
        .results-count {
          margin-bottom: 25px;
          color: #64748b;
          font-size: 14px;
          text-align: right;
        }

        /* Papers Grid */
        .papers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 25px;
          margin-bottom: 40px;
        }

        .paper-card {
          background: white;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.08);
          transition: transform 0.3s, box-shadow 0.3s;
          position: relative;
          overflow: hidden;
        }

        .paper-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.12);
        }

        .paper-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #1a3a6b, #f59e0b);
        }

        .paper-badge {
          display: flex;
          justify-content: space-between;
          margin-bottom: 15px;
        }

        .badge-class {
          background: #1a3a6b;
          color: white;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .badge-exam {
          color: white;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
        }

        .paper-subject {
          color: #1e293b;
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .paper-meta {
          display: flex;
          gap: 15px;
          margin-bottom: 20px;
          font-size: 13px;
          color: #64748b;
        }

        .meta-year, .meta-type {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .download-btn {
          display: block;
          background: #1a3a6b;
          color: white;
          text-align: center;
          padding: 10px;
          border-radius: 10px;
          text-decoration: none;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.3s;
        }

        .download-btn:hover {
          background: #f59e0b;
          transform: translateY(-2px);
        }

        .not-available {
          display: block;
          text-align: center;
          padding: 10px;
          background: #f1f5f9;
          color: #94a3b8;
          border-radius: 10px;
          font-size: 13px;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: white;
          border-radius: 20px;
          margin-bottom: 40px;
        }

        .empty-icon {
          font-size: 64px;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          color: #1a3a6b;
          margin-bottom: 10px;
        }

        .empty-state p {
          color: #64748b;
          margin-bottom: 20px;
        }

        /* Info Section */
        .info-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 25px;
          margin-bottom: 40px;
        }

        .info-card {
          background: white;
          padding: 20px;
          border-radius: 16px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .info-icon {
          font-size: 36px;
          margin-bottom: 12px;
        }

        .info-card h4 {
          color: #1a3a6b;
          margin-bottom: 8px;
        }

        .info-card p {
          color: #64748b;
          font-size: 13px;
          line-height: 1.6;
        }

        /* Back Link */
        .back-link {
          text-align: center;
          margin-top: 20px;
        }

        .back-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #e2e8f0;
          color: #475569;
          padding: 12px 28px;
          border-radius: 40px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s;
        }

        .back-btn:hover {
          background: #1a3a6b;
          color: white;
          transform: translateX(-5px);
        }

        function getExamColor(examType) {
          const colors = {
            'Annual': '#10b981',
            'Half-Yearly': '#f59e0b',
            'Quarterly': '#8b5cf6',
            'Pre-Board': '#ef4444',
            'Monthly Test': '#06b6d4'
          };
          return colors[examType] || '#64748b';
        }

        /* Responsive */
        @media (max-width: 768px) {
          .page-hero {
            padding: 50px 20px;
          }
          
          .page-hero h1 {
            font-size: 32px;
          }
          
          .page-hero p {
            font-size: 16px;
          }

          .filters-grid {
            grid-template-columns: 1fr;
          }

          .papers-grid {
            grid-template-columns: 1fr;
          }

          .results-count {
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
}

function getExamColor(examType) {
  const colors = {
    'Annual': '#10b981',
    'Half-Yearly': '#f59e0b',
    'Quarterly': '#8b5cf6',
    'Pre-Board': '#ef4444',
    'Monthly Test': '#06b6d4'
  };
  return colors[examType] || '#64748b';
}

export default QuestionPapers;